import React, { useEffect, useMemo, useState } from "react";
import "../StockInward.css";
import { getSalesEntries } from "../storage";
import { formatDisplayDate, toComparableYmd } from "../utils/dateFormat";
import { getSettlementSummary, isPaymentSettlement } from "../utils/settlement";

const PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 25, 50, "All"];

function formatMoney(value) {
  return (Number(value) || 0).toFixed(2);
}

function makeDefaultFilters() {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return {
    fromDate: `${year}-${month}-01`,
    toDate: `${year}-${month}-${day}`,
    customer: "",
    search: "",
  };
}

function marketCustomerName(customerText) {
  const raw = String(customerText || "").trim();
  if (!raw) return "Walk-in";
  const lines = raw
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  return lines[0] || "Walk-in";
}

function isEveningSalesSource(value) {
  return String(value || "").trim().toLowerCase() === "evening sales";
}

export default function MarketReports({ category = "Market", title = "Market Reports" }) {
  const [entries, setEntries] = useState([]);
  const [filters, setFilters] = useState(() => makeDefaultFilters());
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [loading, setLoading] = useState(false);
  const normalizedCategory = String(category || "").trim() === "Stall" ? "Stall" : "Market";
  const isStallReport = normalizedCategory === "Stall";
  const lowerCategoryLabel = isStallReport ? "stall credit sales" : "market sales";
  const totalAmountLabel = isStallReport ? "Total Sales" : "Total Purchased";
  const tableAmountLabel = isStallReport ? "Sales Amount" : "Purchased Amount";
  const tableTitle = isStallReport ? "Customer Sales / Payment" : "Customer Purchase / Payment";
  const reportSubtitle = isStallReport
    ? "Customer date-wise stall credit sales and payment report"
    : "Customer date-wise purchase and payment report";

  const refresh = async () => {
    setLoading(true);
    try {
      const list = await getSalesEntries();
      setEntries(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error(err);
      alert(`Failed to load ${lowerCategoryLabel} report data.`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const marketEntries = useMemo(
    () =>
      (Array.isArray(entries) ? entries : []).filter((entry) => {
        const entryCategory = String(entry?.category || "").trim();
        if (entryCategory !== normalizedCategory) return false;
        if (normalizedCategory === "Stall" && isEveningSalesSource(entry?.source)) return false;
        return true;
      }),
    [entries, normalizedCategory]
  );

  const customerOptions = useMemo(() => {
    return Array.from(
      new Set(marketEntries.map((entry) => marketCustomerName(entry?.customer)).filter(Boolean))
    ).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  }, [marketEntries]);

  useEffect(() => {
    if (!filters.customer) return;
    if (!customerOptions.includes(filters.customer)) {
      setFilters((prev) => ({ ...prev, customer: "" }));
    }
  }, [customerOptions, filters.customer]);

  const reportRows = useMemo(() => {
    const map = new Map();

    for (const entry of marketEntries) {
      const customerName = marketCustomerName(entry?.customer);
      const purchaseDate = toComparableYmd(entry?.saleDate);
      const settlementSummary = getSettlementSummary(Number(entry?.total) || 0, entry?.payments);

      if (purchaseDate) {
        const key = `${customerName}__${purchaseDate}`;
        if (!map.has(key)) {
          map.set(key, {
            id: key,
            customerName,
            date: purchaseDate,
            purchaseAmount: 0,
            paidAmount: 0,
            purchaseEntries: 0,
            paymentEntries: 0,
          });
        }
        const row = map.get(key);
        row.purchaseAmount += settlementSummary.totalAmount;
        row.purchaseEntries += 1;
      }

      for (const payment of Array.isArray(entry?.payments) ? entry.payments : []) {
        if (!isPaymentSettlement(payment)) continue;
        const paidAmount = Number(payment?.amount) || 0;
        const paymentDate = toComparableYmd(payment?.date);
        if (!paymentDate || paidAmount <= 0) continue;
        const key = `${customerName}__${paymentDate}`;
        if (!map.has(key)) {
          map.set(key, {
            id: key,
            customerName,
            date: paymentDate,
            purchaseAmount: 0,
            paidAmount: 0,
            purchaseEntries: 0,
            paymentEntries: 0,
          });
        }
        const row = map.get(key);
        row.paidAmount += paidAmount;
        row.paymentEntries += 1;
      }
    }

    return Array.from(map.values())
      .map((row) => ({
        ...row,
        purchaseAmount: +Number(row.purchaseAmount || 0).toFixed(2),
        paidAmount: +Number(row.paidAmount || 0).toFixed(2),
        balanceImpact: +Number((Number(row.purchaseAmount || 0) - Number(row.paidAmount || 0)) || 0).toFixed(2),
      }))
      .sort((a, b) => {
        const dateDiff = String(b.date || "").localeCompare(String(a.date || ""));
        if (dateDiff !== 0) return dateDiff;
        return String(a.customerName || "").localeCompare(String(b.customerName || ""), undefined, {
          sensitivity: "base",
        });
      });
  }, [marketEntries]);

  const filteredRows = useMemo(() => {
    const fromDate = toComparableYmd(filters.fromDate);
    const toDate = toComparableYmd(filters.toDate);
    const customerFilter = String(filters.customer || "").trim();
    const search = String(filters.search || "").trim().toLowerCase();

    return reportRows.filter((row) => {
      const rowDate = toComparableYmd(row?.date);
      if (fromDate && (!rowDate || rowDate < fromDate)) return false;
      if (toDate && (!rowDate || rowDate > toDate)) return false;
      if (customerFilter && String(row?.customerName || "") !== customerFilter) return false;
      if (
        search &&
        !`${String(row?.customerName || "")} ${String(row?.date || "")}`
          .toLowerCase()
          .includes(search)
      ) {
        return false;
      }
      return true;
    });
  }, [reportRows, filters]);

  const totals = useMemo(() => {
    const customers = new Set();
    let purchaseAmount = 0;
    let paidAmount = 0;

    for (const row of filteredRows) {
      if (row.customerName) {
        customers.add(row.customerName);
      }
      purchaseAmount += Number(row.purchaseAmount) || 0;
      paidAmount += Number(row.paidAmount) || 0;
    }

    return {
      customerCount: customers.size,
      rowCount: filteredRows.length,
      purchaseAmount: +purchaseAmount.toFixed(2),
      paidAmount: +paidAmount.toFixed(2),
      balanceImpact: +(purchaseAmount - paidAmount).toFixed(2),
    };
  }, [filteredRows]);

  const effectivePageSize = pageSize === "All" ? Math.max(filteredRows.length, 1) : Number(pageSize) || PAGE_SIZE;
  const totalPages = Math.max(1, Math.ceil(filteredRows.length / effectivePageSize));

  useEffect(() => {
    setPage(1);
  }, [filters.fromDate, filters.toDate, filters.customer, filters.search, pageSize]);

  useEffect(() => {
    setPage((current) => Math.min(current, totalPages));
  }, [totalPages]);

  const pagedRows = useMemo(() => {
    if (pageSize === "All") {
      return filteredRows;
    }
    const start = (page - 1) * effectivePageSize;
    return filteredRows.slice(start, start + effectivePageSize);
  }, [effectivePageSize, filteredRows, page, pageSize]);

  const pageStart = filteredRows.length === 0 ? 0 : (page - 1) * effectivePageSize + 1;
  const pageEnd = filteredRows.length === 0 ? 0 : Math.min(page * effectivePageSize, filteredRows.length);

  return (
    <div className="stock-inward-container" style={{ paddingTop: 0 }}>
      <div className="form-card">
        <div className="card-header" style={{ gap: 12, flexWrap: "wrap" }}>
          <div>
            <h2 className="card-title">{title}</h2>
            <div style={{ fontSize: "0.9rem", color: "#64748b", fontWeight: 600, marginTop: 4 }}>
              {reportSubtitle}
            </div>
          </div>
          <span className="entry-count">Report 1</span>
        </div>
        <div className="inward-form">
          <div className="form-grid">
            <div className="field fl-field">
              <input
                id="market-report-from"
                className="fl-input input-field"
                type="date"
                value={filters.fromDate}
                onChange={(event) => setFilters((prev) => ({ ...prev, fromDate: event.target.value }))}
                placeholder=" "
              />
              <label htmlFor="market-report-from" className="fl-label">
                From Date
              </label>
            </div>
            <div className="field fl-field">
              <input
                id="market-report-to"
                className="fl-input input-field"
                type="date"
                value={filters.toDate}
                onChange={(event) => setFilters((prev) => ({ ...prev, toDate: event.target.value }))}
                placeholder=" "
              />
              <label htmlFor="market-report-to" className="fl-label">
                To Date
              </label>
            </div>
            <div className="field fl-field">
              <select
                id="market-report-customer"
                className="fl-input input-field"
                value={filters.customer}
                onChange={(event) => setFilters((prev) => ({ ...prev, customer: event.target.value }))}
                style={{ paddingTop: "14px" }}
              >
                <option value="">All Customers</option>
                {customerOptions.map((customer) => (
                  <option key={customer} value={customer}>
                    {customer}
                  </option>
                ))}
              </select>
              <label htmlFor="market-report-customer" className="fl-label">
                Customer Name
              </label>
            </div>
            <div className="field fl-field">
              <input
                id="market-report-search"
                className="fl-input input-field"
                type="text"
                value={filters.search}
                onChange={(event) => setFilters((prev) => ({ ...prev, search: event.target.value }))}
                placeholder=" "
              />
              <label htmlFor="market-report-search" className="fl-label">
                Search
              </label>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={() => setFilters(makeDefaultFilters())}>
              Reset Filters
            </button>
            <button type="button" className="btn-secondary" onClick={refresh} disabled={loading}>
              {loading ? "Refreshing..." : "Refresh"}
            </button>
          </div>
        </div>
      </div>

      <div className="entries-card">
        <div className="card-header" style={{ borderBottom: "none" }}>
          <h2 className="card-title">{tableTitle}</h2>
          <span className="entry-count">{filteredRows.length} rows</span>
        </div>

        <div style={{ padding: "0 1.5rem 1rem" }}>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <div
              style={{
                minWidth: 140,
                borderRadius: 14,
                border: "1px solid #cbd5e1",
                background: "#f8fafc",
                padding: "0.9rem 1rem",
              }}
            >
              <div style={{ fontSize: "0.78rem", color: "#64748b", fontWeight: 700 }}>Customers</div>
              <div style={{ fontSize: "1.55rem", fontWeight: 800, color: "#0f172a" }}>{totals.customerCount}</div>
            </div>
            <div
              style={{
                minWidth: 180,
                borderRadius: 14,
                border: "1px solid #dbeafe",
                background: "#eff6ff",
                padding: "0.9rem 1rem",
              }}
            >
              <div style={{ fontSize: "0.78rem", color: "#1d4ed8", fontWeight: 700 }}>{totalAmountLabel}</div>
              <div style={{ fontSize: "1.55rem", fontWeight: 800, color: "#1e3a8a" }}>{formatMoney(totals.purchaseAmount)}</div>
            </div>
            <div
              style={{
                minWidth: 180,
                borderRadius: 14,
                border: "1px solid #bbf7d0",
                background: "#f0fdf4",
                padding: "0.9rem 1rem",
              }}
            >
              <div style={{ fontSize: "0.78rem", color: "#15803d", fontWeight: 700 }}>Total Paid</div>
              <div style={{ fontSize: "1.55rem", fontWeight: 800, color: "#166534" }}>{formatMoney(totals.paidAmount)}</div>
            </div>
            <div
              style={{
                minWidth: 180,
                borderRadius: 14,
                border: "1px solid #fde68a",
                background: "#fffbeb",
                padding: "0.9rem 1rem",
              }}
            >
              <div style={{ fontSize: "0.78rem", color: "#b45309", fontWeight: 700 }}>Balance Impact</div>
              <div style={{ fontSize: "1.55rem", fontWeight: 800, color: "#92400e" }}>{formatMoney(totals.balanceImpact)}</div>
            </div>
          </div>
        </div>

        <div className="entries-table-container">
          <table className="entries-table" style={{ minWidth: 920 }}>
            <thead>
              <tr>
                <th style={{ width: 140 }}>Date</th>
                <th style={{ width: 240 }}>Customer Name</th>
                <th className="text-right" style={{ width: 140 }}>Purchase Entries</th>
                <th className="text-right" style={{ width: 140 }}>Payment Entries</th>
                    <th className="text-right" style={{ width: 160 }}>{tableAmountLabel}</th>
                    <th className="text-right" style={{ width: 160 }}>Paid Amount</th>
                    <th className="text-right" style={{ width: 140 }}>Net</th>
              </tr>
            </thead>
            <tbody>
              {pagedRows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="empty-state" style={{ padding: 16 }}>
                    {`No ${lowerCategoryLabel} report data found for the selected filters.`}
                  </td>
                </tr>
              ) : (
                pagedRows.map((row) => (
                  <tr key={row.id}>
                    <td>{formatDisplayDate(row.date)}</td>
                    <td>{row.customerName}</td>
                    <td className="text-right">{row.purchaseEntries}</td>
                    <td className="text-right">{row.paymentEntries}</td>
                    <td className="text-right" style={{ fontWeight: 700, color: "#1e3a8a" }}>
                      {formatMoney(row.purchaseAmount)}
                    </td>
                    <td className="text-right" style={{ fontWeight: 700, color: "#166534" }}>
                      {formatMoney(row.paidAmount)}
                    </td>
                    <td
                      className="text-right"
                      style={{
                        fontWeight: 700,
                        color: Number(row.balanceImpact || 0) >= 0 ? "#92400e" : "#166534",
                      }}
                    >
                      {formatMoney(row.balanceImpact)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {filteredRows.length > 0 ? (
          <div
            style={{
              padding: "0 1.5rem 1.25rem",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            <div style={{ fontSize: "0.82rem", color: "#475569" }}>
              {`Showing ${pageStart}-${pageEnd} of ${filteredRows.length} rows`}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "0.82rem", color: "#475569" }}>
                <span>Rows</span>
                <select
                  className="input-field compact"
                  value={pageSize}
                  onChange={(event) => {
                    const value = event.target.value;
                    setPageSize(value === "All" ? "All" : Number(value) || PAGE_SIZE);
                  }}
                  style={{ width: 90 }}
                >
                  {PAGE_SIZE_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                disabled={page <= 1 || pageSize === "All"}
                style={{ padding: "0.35rem 0.7rem", fontSize: "0.75rem" }}
              >
                Prev
              </button>
              <span className="entry-count">{`Page ${page} / ${totalPages}`}</span>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={page >= totalPages || pageSize === "All"}
                style={{ padding: "0.35rem 0.7rem", fontSize: "0.75rem" }}
              >
                Next
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
